import type { DtoHistoryRecord } from "@porchpass/api-client";

function humanizeHistoryEventType(eventType: string): string {
  const withSpaces = eventType.replace(/([A-Z])/g, " $1").trim();
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}

function formatHistoryStatus(status: string): string {
  const normalized = status
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .trim()
    .toLowerCase();
  return normalized.replace(/\b\w/g, (character) => character.toUpperCase());
}

function historyDataValue(
  data: DtoHistoryRecord["data"],
  ...keys: string[]
): string | undefined {
  if (!data || typeof data !== "object") {
    return undefined;
  }
  const record = data as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
}

export function formatHistoryDescription(record: DtoHistoryRecord): string {
  const eventType = record.event_type ?? "event";
  const data = record.data;

  switch (eventType) {
    case "taskStatusChanged": {
      const taskName =
        historyDataValue(data, "taskName", "task_name", "title") ?? "Condition";
      const statusTo = historyDataValue(data, "statusTo", "status_to");
      return statusTo
        ? `${taskName} marked as "${formatHistoryStatus(statusTo)}"`
        : `${taskName} status changed`;
    }
    case "taskCreated": {
      const taskName =
        historyDataValue(data, "taskName", "task_name", "title") ?? "Condition";
      return `${taskName} added`;
    }
    case "taskDeleted": {
      const taskName =
        historyDataValue(data, "taskName", "task_name", "title") ?? "Condition";
      return `${taskName} removed`;
    }
    case "noteCreated": {
      const note = historyDataValue(data, "note", "text");
      return note ? `Note added: ${note}` : "Note added";
    }
    case "noteUpdated": {
      const note = historyDataValue(data, "note", "text");
      return note ? `Note updated: ${note}` : "Note updated";
    }
    case "noteDeleted":
      return "Note deleted";
    case "applicationUpdated":
      return "Application updated";
    case "statusTransition": {
      const statusTo = historyDataValue(
        data,
        "statusTo",
        "status_to",
        "status",
      );
      return statusTo
        ? `Application status changed to "${formatHistoryStatus(statusTo)}"`
        : "Application status changed";
    }
    default:
      return humanizeHistoryEventType(eventType);
  }
}

export function historyActor(record: DtoHistoryRecord): string {
  const actor = record.author?.trim();
  const executionName = record.execution_context?.name?.trim();
  return actor || executionName || "System";
}
